"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CountryCodeSelect } from "@/components/ui/CountryCodeSelect";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const projectTypes = [
    "Logo & Branding",
    "UI/UX",
    "Web3",
    "AI Agents",
    "SaaS",
    "3D Art",
    "Motion Graphics",
    "Video",
    "Other"
];

const ContactSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    countryCode: Yup.string().required("Required"),
    mobile: Yup.string().matches(/^[0-9\s-]{8,15}$/, "Invalid number"),
    telegram_id: Yup.string().min(3, "Too Short"),
    message: Yup.string().min(10, "Tell us a bit more!").required("Required"),
});

export const ContactSection = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            countryCode: "+91",
            mobile: "",
            telegram_id: "",
            project_types: [] as string[],
            message: "",
        },
        validationSchema: ContactSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            setError(null);
            
            try {
                // Combine country code and mobile for submission
                const fullMobile = values.mobile ? `${values.countryCode} ${values.mobile}` : "";
                const { countryCode, ...rest } = values;
                const submissionData = { ...rest, mobile: fullMobile };

                const response = await fetch("/api/contact", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(submissionData),
                });

                if (response.ok) {
                    setSubmitted(true);
                    formik.resetForm();
                } else {
                    const data = await response.json();
                    setError(data.error || "Submission failed. Please try again.");
                }
            } catch (err) {
                setError("Something went wrong. Check your connection.");
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const toggleProjectType = (type: string) => {
        const currentTypes = formik.values.project_types;
        if (currentTypes.includes(type)) {
            formik.setFieldValue("project_types", currentTypes.filter(t => t !== type));
        } else {
            formik.setFieldValue("project_types", [...currentTypes, type]);
        }
    };

    return (
        <section id="contact" className="relative pt-60 pb-40 px-4 bg-white text-black overflow-hidden border-t border-black/5">
            <div className="max-w-[1200px] mx-auto space-y-24">
                {/* HEADLINE & SUBTEXT */}
                <div className="space-y-12">
                    <motion.h2 
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display text-[12vw] md:text-[10vw] leading-[1.1] uppercase tracking-tighter"
                    >
                        Let’s build <br />
                        something <span className="text-primary italic">real.</span>
                    </motion.h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-black/10 pt-12 items-start">
                        <div className="space-y-8">
                            <p className="font-sans text-xl md:text-2xl text-black/70 uppercase leading-snug tracking-tighter max-w-md">
                                For founders who don’t want to move slow. Tell us about your idea. We’ll take care of the execution.
                            </p>
                            <a 
                                href="https://cal.com/dotslashlab" 
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Book an intro call" 
                                className="flex items-center gap-3 px-8 py-4 border-2 border-black rounded-full font-display text-xl uppercase hover:bg-black hover:text-white transition-all group"
                            >
                                Book an intro call
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                            </a>
                        </div>
                        <div className="flex flex-col gap-6">
                            <p className="font-sans text-xl text-black/60 uppercase tracking-tighter leading-snug">
                                Built with intent. Execution over noise. Products that ship.
                            </p>
                            <p className="font-sans text-xl text-black/60 uppercase tracking-tighter leading-snug">
                                You can book a call or just drop us a message — either way, we’d love to hear from you.
                            </p>
                        </div>
                    </div>
                </div>

                {/* FORM AREA */}
                <form onSubmit={formik.handleSubmit} className="space-y-12">
                    {/* Project Selection */}
                    <div className="space-y-6">
                        <h3 className="font-display text-2xl uppercase tracking-tight">What can we do for you ?</h3>
                        <div className="flex flex-wrap gap-3">
                            {projectTypes.map((type) => {
                                const isSelected = formik.values.project_types.includes(type);
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => toggleProjectType(type)}
                                        className={`px-6 py-3 rounded-full border border-black/10 text-sm font-sans uppercase tracking-widest transition-all ${
                                            isSelected ? "bg-black text-white border-black" : "hover:border-black/30"
                                        }`}
                                    >
                                        {type}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <input
                                name="name"
                                type="text"
                                placeholder="NAME"
                                aria-label="Your Name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                className={`w-full px-8 py-6 bg-slate-50 rounded-xl font-sans text-sm focus:outline-none focus:ring-1 focus:ring-black/10 transition-all ${
                                    formik.touched.name && formik.errors.name ? "ring-red-500" : ""
                                }`}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <span className="text-[10px] text-red-500 uppercase pl-4">{formik.errors.name}</span>
                            )}
                        </div>
                        <div className="space-y-1">
                            <input
                                name="email"
                                type="email"
                                placeholder="EMAIL ADDRESS"
                                aria-label="Email Address"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                className={`w-full px-8 py-6 bg-slate-50 rounded-xl font-sans text-sm focus:outline-none focus:ring-1 focus:ring-black/10 transition-all ${
                                    formik.touched.email && formik.errors.email ? "ring-red-500" : ""
                                }`}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <span className="text-[10px] text-red-500 uppercase pl-4">{formik.errors.email}</span>
                            )}
                        </div>

                        {/* Mobile & Telegram ID */}
                        <div className="space-y-1">
                            <div className="flex bg-slate-50 rounded-xl focus-within:ring-1 focus-within:ring-black/10 transition-all">
                                <CountryCodeSelect 
                                    value={formik.values.countryCode} 
                                    onChange={(code) => formik.setFieldValue("countryCode", code)} 
                                />
                                <input
                                    name="mobile"
                                    type="text"
                                    placeholder="MOBILE NUMBER"
                                    aria-label="Mobile Number"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.mobile}
                                    className={`flex-1 px-8 py-6 bg-transparent rounded-r-xl font-sans text-sm focus:outline-none ${
                                        formik.touched.mobile && formik.errors.mobile ? "text-red-500" : ""
                                    }`}
                                />
                            </div>
                            {(formik.touched.mobile && formik.errors.mobile) || (formik.touched.countryCode && formik.errors.countryCode) ? (
                                <span className="text-[10px] text-red-500 uppercase pl-4">
                                    {formik.errors.mobile || formik.errors.countryCode}
                                </span>
                            ) : null}
                        </div>
                        <div className="space-y-1">
                            <input
                                name="telegram_id"
                                type="text"
                                placeholder="TELEGRAM ID (e.g. @username)"
                                aria-label="Telegram ID"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.telegram_id}
                                className={`w-full px-8 py-6 bg-slate-50 rounded-xl font-sans text-sm focus:outline-none focus:ring-1 focus:ring-black/10 transition-all ${
                                    formik.touched.telegram_id && formik.errors.telegram_id ? "ring-red-500" : ""
                                }`}
                            />
                            {formik.touched.telegram_id && formik.errors.telegram_id && (
                                <span className="text-[10px] text-red-500 uppercase pl-4">{formik.errors.telegram_id}</span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-1">
                        <textarea
                            name="message"
                            rows={6}
                            placeholder="Tell us what's on your mind—big ideas, small details, or anything in between..."
                            aria-label="Your Message"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.message}
                            className={`w-full px-8 py-6 bg-slate-50 rounded-xl font-sans text-sm focus:outline-none focus:ring-1 focus:ring-black/10 transition-all resize-none ${
                                formik.touched.message && formik.errors.message ? "ring-red-500" : ""
                            }`}
                        />
                        {formik.touched.message && formik.errors.message && (
                            <span className="text-[10px] text-red-500 uppercase pl-4">{formik.errors.message}</span>
                        )}
                    </div>

                    {/* Submit & Success/Error States */}
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <button
                            type="submit"
                            disabled={isSubmitting || submitted}
                            className="group relative px-16 py-8 bg-black text-white font-display text-3xl uppercase rounded-xl hover:scale-105 transition-all active:scale-95 overflow-hidden disabled:opacity-50 disabled:hover:scale-100"
                        >
                            <span className="relative z-10">{isSubmitting ? "Sending..." : "Send Proposal"}</span>
                            <div className="absolute inset-0 bg-primary translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                        </button>

                        <AnimatePresence>
                            {submitted && (
                                <motion.div 
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="flex items-center gap-3 text-emerald-600 font-sans font-bold uppercase tracking-widest text-sm"
                                >
                                    <CheckCircle2 className="w-6 h-6" />
                                    Proposal Sent Successfully
                                </motion.div>
                            )}
                            {error && (
                                <motion.div 
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-red-500 font-sans font-bold uppercase tracking-widest text-sm"
                                >
                                    {error}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </form>
            </div>
            
            {/* Background premium detail: Subtle noise */}
            <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none" />
        </section>
    );
};
